import {
  LayoutDashboard,
  Network,
  Server,
  Users,
  ShoppingCart,
  FileText,
  HelpCircle,
  Radio,
  Wifi,
  Handshake,
  GitBranch,
  Router,
  MapPin,
  Package,
  FolderOpen,
  Receipt,
  Briefcase,
  CircuitBoard,
  FileDown,
  Activity,
  AlertTriangle,
  ShieldCheck,
  History,
  Send,
  BadgePercent,
  BadgeDollarSign,
  Megaphone,
  Coins,
  Boxes,
  BookLock,
  Settings2,
  ClipboardList,
  FileStack,
  ShieldAlert,
  IdCard,
  Warehouse,
} from 'lucide-react'
import { type SidebarData } from '../types'

/**
 * Roles whose menu is limited to these pages regardless of their read
 * permissions (those still feed dialogs, e.g. Teknisi Lapangan opens Setup
 * Instalasi / Ganti ONT from Barang Saya). The first entry is their home page.
 */
export const ROLE_MENU_ONLY: Record<string, string[]> = {
  field_technician: ['/inventory/me'],
}

export function menuAllowedForRole(role: string | undefined, url?: string) {
  const only = role ? ROLE_MENU_ONLY[role] : undefined
  return !only || (!!url && only.includes(url))
}

export const sidebarData: SidebarData = {
  user: {
    name: 'Admin',
    email: 'admin@datanet.id',
    avatar: '/avatars/shadcn.jpg',
  },
  teams: [
    {
      name: 'DataNet',
      logo: Network,
      plan: 'ISP Management',
    },
  ],
  navGroups: [
    {
      title: 'Dashboard',
      items: [
        {
          title: 'Overview',
          url: '/',
          icon: LayoutDashboard,
          requiredPermission: 'dashboard.read',
        },
        {
          title: 'Monitoring',
          url: '/monitoring',
          icon: Server,
          requiredPermission: 'monitoring.read',
        },
        {
          title: 'ONU Monitoring',
          url: '/monitoring/onus',
          icon: Activity,
          requiredPermission: 'monitoring.read',
        },
        {
          title: 'Status Mismatch',
          url: '/monitoring/status-mismatch',
          icon: AlertTriangle,
          requiredPermission: 'monitoring.read',
        },
      ],
    },
    {
      title: 'CRM',
      items: [
        {
          title: 'Customers',
          url: '/customers',
          icon: Users,
          requiredPermission: 'customers.read',
        },
        {
          title: 'Orders',
          url: '/orders',
          icon: ShoppingCart,
          requiredPermission: 'orders.read',
        },
        {
          title: 'Subscriptions',
          url: '/subscriptions',
          icon: Wifi,
          requiredPermission: 'subscriptions.read',
        },
        {
          title: 'Penyedia',
          url: '/subscriptions/providers',
          icon: Handshake,
          requiredPermission: 'providers.update',
        },
      ],
    },
    {
      title: 'Billing',
      items: [
        {
          title: 'Invoices',
          url: '/billing/invoices',
          icon: Receipt,
          requiredPermission: 'invoices.read',
        },
        {
          title: 'Hub Invoice',
          url: '/billing/hub-invoices',
          icon: FileText,
          requiredPermission: 'hub_invoices.read',
        },
        {
          title: 'Billing Cycle',
          url: '/billing/billing-cycles',
          icon: FileText,
          requiredPermission: 'billing_cycles.read',
        },
        {
          title: 'Diskon Subscription',
          url: '/billing/discounts',
          icon: BadgePercent,
          requiredPermission: 'discounts.read',
        },
        {
          title: 'Voucher Diskon',
          url: '/billing/discount-vouchers',
          icon: BadgeDollarSign,
          requiredPermission: 'discount_vouchers.read',
        },
        {
          title: 'Poin Customer',
          url: '/billing/customer-points',
          icon: Coins,
          requiredPermission: 'customer_points.read',
        },
      ],
    },
    {
      title: 'Marketing',
      items: [
        {
          title: 'Broadcast',
          url: '/marketing/broadcast',
          icon: Send,
          requiredPermission: 'marketing.read',
        },
        {
          title: 'Template WA',
          url: '/marketing/templates',
          icon: Megaphone,
          requiredPermission: 'marketing.read',
        },
      ],
    },
    {
      title: 'Product',
      items: [
        {
          title: 'Category',
          url: '/products/categories',
          icon: FolderOpen,
          requiredPermission: 'product_categories.read',
        },
        {
          title: 'Products',
          url: '/products',
          icon: Package,
          requiredPermission: 'products.read',
        },
      ],
    },
    {
      title: 'Devices',
      items: [
        {
          title: 'OLT',
          url: '/devices/olts',
          icon: Radio,
          requiredPermission: 'olts.read',
        },
        {
          title: 'OLT Card',
          url: '/devices/olt-cards',
          icon: CircuitBoard,
          requiredPermission: 'olts.read',
        },
        {
          title: 'Splitter',
          url: '/devices/splitters',
          icon: GitBranch,
          requiredPermission: 'splitters.read',
        },
        {
          title: 'ONU',
          url: '/devices/onus',
          icon: Router,
          requiredPermission: 'onus.read',
        },
        {
          title: 'Mikrotik',
          url: '/devices/mikrotiks',
          icon: Server,
          requiredPermission: 'mikrotik.read',
        },
      ],
    },
    {
      title: 'Inventori',
      items: [
        {
          title: 'Barang Saya',
          url: '/inventory/me',
          icon: ClipboardList,
          requiredPermission: 'inventory_requests.create',
        },
        {
          title: 'Gudang',
          url: '/inventory/warehouse',
          icon: Warehouse,
          requiredPermission: 'inventory_approve.update',
        },
        {
          title: 'Pengawasan',
          url: '/inventory/control',
          icon: ShieldAlert,
          requiredPermission: 'inventory_ledger.read',
        },
        {
          title: 'Stok',
          url: '/inventory/stock',
          icon: Boxes,
          requiredPermission: 'inventory_ledger.read',
        },
        {
          title: 'Semua Dokumen',
          url: '/inventory/documents',
          icon: FileStack,
          requiredPermission: 'inventory_requests.read',
        },
        {
          title: 'Ledger Stok',
          url: '/inventory/ledger',
          icon: BookLock,
          requiredPermission: 'inventory_ledger.read',
        },
        {
          title: 'Master Inventori',
          url: '/inventory/master',
          icon: Settings2,
          requiredPermission: 'inventory_master.read',
        },
      ],
    },
    {
      title: 'User dan Area',
      items: [
        {
          title: 'Coverage Area',
          url: '/devices/coverage-areas',
          icon: MapPin,
          requiredPermission: 'coverage_areas.read',
        },
        {
          title: 'Partner',
          url: '/partners',
          icon: Briefcase,
          requiredPermission: 'partners.read',
        },
        {
          title: 'Users',
          url: '/users',
          icon: Users,
          requiredPermission: 'users.read',
        },
        {
          title: 'Karyawan',
          url: '/employees',
          icon: IdCard,
          requiredPermission: 'employees.read',
        },
        {
          title: 'Roles & Permissions',
          url: '/roles',
          icon: ShieldCheck,
          requiredPermission: 'roles.read',
        },
        {
          title: 'Audit Log',
          url: '/audit-logs',
          icon: Activity,
          requiredPermission: 'logs.read',
        },
      ],
    },
    {
      title: 'Operation',
      items: [
        {
          title: 'Billing Run History',
          url: '/operation/billing-run-history',
          icon: History,
          requiredPermission: 'billing.run',
        },
        {
          title: 'Billing Engine Run',
          url: '/operation/billing-engine-run',
          icon: Send,
          requiredPermission: 'billing.run',
        },
      ],
    },
    {
      title: 'Utility',
      items: [
        {
          title: 'Downloads',
          url: '/monitoring/downloads',
          icon: FileDown,
          requiredPermission: 'monitoring.read',
        },
        {
          title: 'Help Center',
          url: '/help-center',
          icon: HelpCircle,
        },
        {
          title: 'Celery Tasks',
          url: '/celery',
          icon: Activity,
          requiredPermission: 'celery.read',
        },
      ],
    },
  ],
}

/** First page a user may open, in sidebar order (home for users who can't
 * see the dashboard). */
export function firstAllowedUrl(
  can: (permission: string) => boolean,
  role?: string
): string | null {
  const only = role ? ROLE_MENU_ONLY[role] : undefined
  if (only) return only[0]
  for (const group of sidebarData.navGroups)
    for (const item of group.items) {
      const links = item.items
        ? item.items.map((x) => ({
            ...x,
            requiredPermission: item.requiredPermission,
          }))
        : [item]
      for (const link of links)
        if (
          link.url &&
          (!link.requiredPermission || can(link.requiredPermission))
        )
          return link.url as string
    }
  return null
}
